import { AuthProvider } from '@AuthProvider';
import { useAuth, useUser } from '@clerk/react';
import SplashLoader from '@components/atoms/SplashLoader';
import { useQuery } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { ABTestContextProvider } from 'src/contexts/ABTestContext';
import { ApplicationContextProvider } from 'src/contexts/ApplicationContext';
import { useClerkMe } from 'src/contexts/ClerkMeContext';
import { ListingContextProvider } from 'src/contexts/ListingContext';
import { PolicyContextProvider } from 'src/contexts/PolicyContext';
import { ReputationContextProvider } from 'src/contexts/ReputationContext';
import { SynupContstantsProvider } from 'src/contexts/SynupConstantsContext';
import { isClerkAuthEnabled } from 'src/utils/clerkAuth';

const PrivateRouteContent: React.FC = () => (
  <AuthProvider>
    <ABTestContextProvider>
      <PolicyContextProvider>
        <SynupContstantsProvider>
          <ApplicationContextProvider>
            <ListingContextProvider>
              <ReputationContextProvider>
                <Outlet />
              </ReputationContextProvider>
            </ListingContextProvider>
          </ApplicationContextProvider>
        </SynupContstantsProvider>
      </PolicyContextProvider>
    </ABTestContextProvider>
  </AuthProvider>
);

const ClerkPrivateGate: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();
  const me = useClerkMe();
  const memberships = useQuery({
    queryKey: ['clerkMemberships', user?.id ?? null],
    queryFn: async () => {
      if (!user) throw new Error('Clerk user not loaded');
      return user.getOrganizationMemberships();
    },
    enabled: isLoaded && isSignedIn === true && userLoaded && Boolean(user?.id),
  });

  const membershipCount = memberships.data
    ? Math.max(memberships.data.total_count ?? 0, memberships.data.data.length)
    : null;
  if (!isLoaded || (isSignedIn && (me.isPending || (memberships.fetchStatus === 'fetching' && !memberships.data))))
    return <SplashLoader />;
  if (!isSignedIn) return <Navigate to="/auth" />;
  if (membershipCount === 0) return <Navigate to="/auth/trial" replace />;
  if (memberships.isError || me.isError) return <Navigate to="/auth/no-account" replace />;
  // Policy resolves activeAccount from the active Clerk org, so a user with several accounts is admitted.
  if (!me.data?.activeAccount) return <Navigate to="/auth/no-account" replace />;

  return <PrivateRouteContent />;
};
const OutsetaPrivateGate: React.FC = () => {
  const token = localStorage.getItem('Outseta.nocode.accessToken');

  if (!token) return <Navigate to="/auth" />;

  // Round 10 review (H1): MainRouter.tsx's two PropertyGuardRoute siblings each treat a
  // jwtDecode failure as "predicate false" and Navigate to the OTHER guard's redirectTo —
  // and since neither guard's redirectTo is /auth, a garbage/undecodable token (e.g. a
  // crafted `access_token` URL param written straight to localStorage by MagicLinkPage with
  // no server-side shape check) bounces between the two guards forever instead of landing
  // anywhere. That's worse than the crash it was meant to fix (silent infinite loop vs. a
  // visible one-time error). Catching it here instead — the single entry point both guard
  // siblings sit under — means a garbage token never reaches either of them: it's treated
  // exactly like "no token" (this function's own check just above), landing on /auth and
  // clearing the bad value so a retry doesn't loop.
  try {
    jwtDecode(token);
  } catch {
    localStorage.removeItem('Outseta.nocode.accessToken');
    return <Navigate to="/auth" />;
  }

  return <PrivateRouteContent />;
};

export const PrivateRoute: React.FC = () => {
  const { from_mail } = useParams();
  if (from_mail) {
    sessionStorage.setItem('from_mail', from_mail);
  }

  return isClerkAuthEnabled() ? <ClerkPrivateGate /> : <OutsetaPrivateGate />;
};
